import { BenefitDTOBenefitEnum, CouponsBenefitOptionsDTO, CreateNonCashBenefitUsageDTONonCashBenefitEnum } from 'probonio-shared-ui/api';
import { useMemo } from 'react';
import { fillNumberRangeArray } from '../../../util/numberRange';
import { useWellpassSettings } from '../wellpass/useWellpassSettings';

export const NON_CASH_BENEFITS: BenefitDTOBenefitEnum[] & NonCashBenefit[] = [
  BenefitDTOBenefitEnum.Fitness,
  BenefitDTOBenefitEnum.Coupons,
  BenefitDTOBenefitEnum.CreditCard,
  BenefitDTOBenefitEnum.Wellpass,
];
export type NonCashBenefit = CreateNonCashBenefitUsageDTONonCashBenefitEnum;
export const NonCashBenefit = CreateNonCashBenefitUsageDTONonCashBenefitEnum;

// TODO this value should be configurable per tax period (TaxConfig)
const MIN = 5;
const MAX = 50;
const BUDGET_OPTIONS = fillNumberRangeArray(MIN, MAX, 5);
BUDGET_OPTIONS.push(44);
BUDGET_OPTIONS.sort((a, b) => a - b);

/** Euro amounts selectable per non-cash benefit. WELLPASS is not listed here — use {@link wellpassPossibleAmountsEuro} with `tenant.wellpassBudget` (cents). */
export const possibleAmounts: Partial<Record<NonCashBenefit, number[]>> = {
  FITNESS: [25, 40, 50],
  COUPONS: BUDGET_OPTIONS,
};

export const wellpassPossibleAmountsEuro = (wellpassBudget: number | null | undefined): number[] =>
  wellpassBudget ? [wellpassBudget / 100] : [];

/**
 * Coupons can only be ordered in fixed steps ({@link possibleAmounts}.COUPONS). A budget
 * like 44.32 € must be mapped down to the largest orderable step (40 €) to determine which coupon
 * providers are actually available. Takes and returns cents; returns 0 below the smallest step.
 */
export const floorToOrderableCouponAmount = (amountCents: number): number =>
  (possibleAmounts.COUPONS ?? []).reduce((best, step) => {
    const stepCents = step * 100;
    return stepCents <= amountCents && stepCents > best ? stepCents : best;
  }, 0);

export const useNonCashBenefitAmounts = () => {
  const { data: wellpassSettings, isLoading: isWellpassLoading } = useWellpassSettings();

  return useMemo(() => {
    const wellpassConfigured = !!wellpassSettings?.wellpassBudget;
    const byBenefit: Partial<Record<NonCashBenefit, number[]>> = {
      ...possibleAmounts,
      [NonCashBenefit.Wellpass]: wellpassConfigured ? wellpassPossibleAmountsEuro(wellpassSettings.wellpassBudget) : [],
    };

    const amountsFor = (benefit: NonCashBenefit): number[] => byBenefit[benefit] ?? [];

    const isValid = (benefit: NonCashBenefit, amount: number): boolean => {
      // While wellpassSettings is still loading we don't know whether the amount is valid.
      // Return true so the useEffect in SelectNonCashBenefitsForm does not evict a WELLPASS
      // selection that was already made. The toggle itself is disabled separately in
      // NonCashBenefitCard until loading completes.
      if (benefit === NonCashBenefit.Wellpass && isWellpassLoading) return true;
      const allowed = byBenefit[benefit];
      if (!allowed || allowed.length === 0) return false;
      return amount >= Math.min(...allowed);
    };

    return { amountsFor, isValid, isLoading: isWellpassLoading };
  }, [isWellpassLoading, wellpassSettings]);
};

export type NonCashBenefitOptions = CouponsBenefitOptionsDTO;
